import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ApiError } from '@/api/client';
import { useCompleteExternalLink, useCompleteExternalLogin } from '@/api/queries/auth';
import { Button } from '@pixlide/ui';
import { AuthLayout, FormAlert } from '@/features/auth/AuthLayout';
import { TwoFactorChallengeStep } from '@/features/auth/TwoFactorChallengeStep';
import { consumePendingExternalAuth, providerLabel } from '@/features/auth/external-provider';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Messages indexés par le CODE stable de l'API, comme sur `LoginPage`. Chacun indique quoi
 * faire ensuite : un refus de connexion externe est presque toujours réparable, mais jamais
 * devinable — sans cette table, l'utilisateur ne verrait qu'un « connexion impossible ».
 */
const ERROR_MESSAGES: Record<string, string> = {
  'auth.external_link_required':
    'Un compte Pixlide existe déjà avec cette adresse. Connectez-vous avec votre mot de passe, puis associez ce mode de connexion depuis Paramètres › Sécurité.',
  'auth.external_account_not_found':
    "Aucun compte Pixlide n'est associé à cette adresse. Créez votre compte, ou acceptez l'invitation reçue par email.",
  'auth.external_email_unverified':
    "Votre adresse email n'est pas vérifiée chez ce fournisseur. Vérifiez-la de son côté, puis réessayez.",
  'auth.external_state_invalid':
    'Cette demande a expiré ou ne correspond pas à ce navigateur. Recommencez la connexion.',
  'auth.external_identity_already_used':
    'Ce compte externe est déjà associé à un autre compte Pixlide.',
  'auth.external_provider_unreachable':
    'Le fournisseur de connexion est momentanément injoignable. Réessayez dans un instant.',
  'auth.account_disabled': 'Ce compte a été désactivé. Contactez le propriétaire du compte.',
  'auth.account_locked':
    'Votre compte est temporairement bloqué après plusieurs tentatives. Réessayez dans quelques minutes.',
};

/**
 * Écran de retour du fournisseur externe.
 *
 * Il n'affiche rien de durable : il consomme le code d'autorisation déposé dans l'URL,
 * appelle l'API, puis s'efface au profit de la destination. Sa seule raison d'exister est
 * que le fournisseur ne sait rediriger que vers une URI FIXE, déclarée à l'avance chez lui.
 *
 * Le code d'autorisation traîne dans la barre d'adresse, donc dans l'historique : toutes les
 * navigations de sortie utilisent `replace` pour l'en retirer au plus tôt.
 */
export function ExternalAuthCallbackPage() {
  const navigate = useNavigate();
  const { provider } = useParams({ from: '/connexion/retour/$provider' });
  const search = useSearch({ from: '/connexion/retour/$provider' });
  const login = useCompleteExternalLogin();
  const link = useCompleteExternalLink();

  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // L'access token vit UNIQUEMENT en mémoire (voir `auth-store`), et l'aller-retour chez le
  // fournisseur est une navigation complète : la session courante a donc été effacée en
  // partant. `main.tsx` la restaure depuis le cookie de rafraîchissement, mais en parallèle
  // du premier rendu — cet écran doit attendre la fin de cette restauration.
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);

  // L'échange ne doit avoir lieu qu'UNE fois : un code d'autorisation est à usage unique.
  // Sans ce garde-fou, le double montage du mode strict de React le rejouerait, et le
  // second appel afficherait une erreur sur une connexion pourtant réussie.
  const exchanged = useRef(false);

  useEffect(() => {
    // Sans cette attente, l'association part sans jeton d'accès et l'API la refuse en 401
    // avant même d'atteindre le handler — un échec parfaitement silencieux côté serveur,
    // puisque rien n'est journalisé pour une requête rejetée par l'authentification.
    // La connexion, elle, n'a pas besoin de session : attendre reste sans conséquence pour
    // elle, et évite deux chemins de démarrage différents sur le même écran.
    if (!isBootstrapped || exchanged.current) {
      return;
    }

    exchanged.current = true;

    void (async () => {
      const pending = consumePendingExternalAuth();

      // Refus explicite chez le fournisseur (bouton « Annuler » de son écran de consentement).
      if (search.error !== undefined) {
        setFailure('La connexion a été refusée chez le fournisseur. Vous pouvez réessayer.');

        return;
      }

      if (pending === null || search.code === undefined || search.state === undefined) {
        setFailure(ERROR_MESSAGES['auth.external_state_invalid']!);

        return;
      }

      const payload = { provider: pending.provider, code: search.code, state: search.state };

      try {
        if (pending.intent === 'link') {
          await link.mutateAsync(payload);
          await navigate({ to: '/parametres', replace: true });

          return;
        }

        const result = await login.mutateAsync(payload);

        if (result.twoFactorRequired && result.twoFactorChallengeToken) {
          setChallengeToken(result.twoFactorChallengeToken);

          return;
        }

        await navigate({ to: '/', replace: true });
      } catch (error) {
        const apiError = error instanceof ApiError ? error : null;

        setFailure(
          (apiError === null ? null : (ERROR_MESSAGES[apiError.code] ?? apiError.message)) ??
            'La connexion a échoué. Réessayez.',
        );
      }
    })();

    // `isBootstrapped` seul, et la règle est désactivée en connaissance de cause : réagir
    // à un changement de `search` ou de `navigate` relancerait l'échange d'un code
    // d'autorisation déjà consommé. Le garde `exchanged` couvre le reste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBootstrapped]);

  if (challengeToken !== null) {
    return <TwoFactorChallengeStep challengeToken={challengeToken} />;
  }

  if (failure !== null) {
    return (
      <AuthLayout
        title="Connexion impossible"
        subtitle={`La connexion via ${providerLabel(provider)} n'a pas abouti.`}
      >
        <FormAlert message={failure} />

        <Link to="/connexion" className="block">
          <Button className="h-11 w-full rounded-xl">Revenir à la connexion</Button>
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Connexion en cours"
      subtitle={`Vérification de votre identité auprès de ${providerLabel(provider)}.`}
    >
      <p role="status" className="text-[13px] text-ink-500">
        Un instant, ne fermez pas cette page.
      </p>
    </AuthLayout>
  );
}
